import React, { useEffect, useState } from 'react';
import { useGetTenantBrandQuery, useGetUserDataQuery, useUpdatePasswordMutation, useUpdateUserDataMutation } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import { BoxArrowRight, Gear, Gem, Grid1x2, PersonCircle } from 'react-bootstrap-icons';
import { Button, Dropdown, Modal, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken } from '../services/GlobalSlice';
import { toast } from 'react-toastify';
import { userApi } from '../services/user';
import './UserMenu.css';

const UserDropdown = ({ handleLogout, openSettings, openUpgradePlan }) => {
  const navigate = useNavigate();
  const { data: userData, isLoading, error } = useGetUserDataQuery();
  const { theme } = useSelector((state) => state.theme); // Get theme from Redux

  useEffect(() => {
    if (error) {
      navigate('/login', { replace: true });
    }
  }, [error, navigate]);

  if (isLoading) {
    return <PersonCircle size={20} color="gray" />;
  }

  return (
    <Dropdown>
      <Dropdown.Toggle
        as="div"
        bsPrefix="custom-toggle"
        id="user-dropdown"
        className="d-flex align-items-center"
        style={{ cursor: 'pointer' }}
      >
        <PersonCircle size={20} color={theme === 'dark' ? 'rgba(255,255,255,0.5)' : '#6b7280'} />
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" className="dropdown-menu">
        {userData?.is_superuser && (
          <Dropdown.Item
            className="admin-option"
            onClick={() => navigate('/admin-dashboard')}
          >
            <Grid1x2 className="me-2" /> Admin Dashboard
          </Dropdown.Item>
        )}
        {((userData?.role || '').toLowerCase() === 'tenant_admin' || userData?.is_staff === true) && (
          <Dropdown.Item
            className="admin-option"
            onClick={() => navigate('/tenant-admin')}
          >
            <Grid1x2 className="me-2" /> Workspace Admin
          </Dropdown.Item>
        )}
        <Dropdown.Item onClick={openSettings}>
          <Gear className="me-2" /> Settings
        </Dropdown.Item>
        <Dropdown.Item onClick={openUpgradePlan}>
          <Gem className="me-2" /> Upgrade Plan
        </Dropdown.Item>
        <Dropdown.Item onClick={handleLogout}>
          <BoxArrowRight className="me-2" /> Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const UserMenu = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgradePlan, setShowUpgradePlan] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: userData, refetch } = useGetUserDataQuery();
  const { data: brandData } = useGetTenantBrandQuery();
  const [updateUserData] = useUpdateUserDataMutation();
  const [updatePassword] = useUpdatePasswordMutation();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const handleUpdate = async () => {
    try {
      await updateUserData(formData).unwrap();
      toast.success('Profile updated successfully!');
      refetch();
    } catch {
      toast.error('Failed to update profile.');
    }
  };
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
      });
    }
  }, [userData]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    try {
      await updatePassword({
        old_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      }).unwrap();
      toast.success('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '' });
      setShowUpdatePassword(false);
    } catch (error) {
      toast.error(error?.data?.error || 'Failed to update password.');
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('hasLogin');
    localStorage.removeItem('user');
    dispatch(setAuthToken(null));
    dispatch(userApi.util.resetApiState());
    toast.success('Signed out successfully.');
    navigate('/login');
  };

  return (
    <>
      <UserDropdown
        handleLogout={handleLogout}
        openSettings={() => setShowSettings(true)}
        openUpgradePlan={() => setShowUpgradePlan(true)}
      />

      {/* Settings Modal */}
      <Modal show={showSettings} onHide={() => setShowSettings(false)} centered contentClassName="app-modal-content">
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="w-100 text-center fw-bold">Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center p-4">
          <Form className="w-100" style={{ maxWidth: '400px' }}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">First Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Workspace</Form.Label>
              <Form.Control type="text" value={brandData?.tenant_name || 'Not assigned yet'} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Email</Form.Label>
              <Form.Control type="text" value={formData.email} disabled />
            </Form.Group>
            <div className="d-flex flex-column gap-3 align-items-center mt-4">
              <Button variant="dark" className="w-100 py-2 fw-semibold" onClick={handleUpdate}>
                Save Changes
              </Button>
              <Button
                variant="link"
                className="text fw-semibold"
                onClick={() => {
                  setShowSettings(false);
                  setShowUpdatePassword(true);
                }}
              >
                Reset Password
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal show={showUpgradePlan} onHide={() => setShowUpgradePlan(false)} centered contentClassName="app-modal-content">
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">Upgrade Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center gap-3 text-center">
          <p className="mb-0">Contact us to upgrade your plan.</p>
          <Button
            variant="primary"
            href="mailto:support@stratusedge.ai"
            as="a"
          >
            support@stratusedge.ai
          </Button>
        </Modal.Body>
      </Modal>

      {/* Update Password Modal */}
      <Modal show={showUpdatePassword} onHide={() => setShowUpdatePassword(false)} centered contentClassName="app-modal-content">
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">Update Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUpdatePassword}>
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label">
                Current Password
              </label>
              <input
                type="password"
                className="form-control"
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <input
                type="password"
                className="form-control"
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="d-flex justify-content-center">
              <Button type="submit" variant="dark" className="w-100 py-2 fw-semibold">
                Update Password
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserMenu;
